Controller.$inject = ['$element', '$scope'];

function Controller($element, $scope) {
    const self = this;
    self.isPlaying = false;
    self.durationSec = 0;
    self.currentTimeSec = 0;
    self.playbackRate = Number(self.playbackRate) || 1;

    function getAudioEl() {
        return $element[0].querySelector('audio');
    }

    function getSafePlaybackRate(rate) {
        const numericRate = Number(rate);
        if (!isFinite(numericRate) || numericRate <= 0) {
            return 1;
        }
        return Math.min(4, Math.max(0.25, numericRate));
    }

    function attachAudioListeners() {
        const audio = getAudioEl();
        if (!audio) {
            return false;
        }
        audio.playbackRate = getSafePlaybackRate(self.playbackRate);
        self._timeUpdateHandler = self.onTimeUpdate.bind(self);
        self._loadedMetadataHandler = self.onLoadedMetadata.bind(self);
        self._endedHandler = self.onEnded.bind(self);
        audio.addEventListener('timeupdate', self._timeUpdateHandler);
        audio.addEventListener('loadedmetadata', self._loadedMetadataHandler);
        audio.addEventListener('ended', self._endedHandler);
        return true;
    }

    self.$postLink = () => {
        attachAudioListeners();
    };

    self.togglePlay = () => {
        const audio = getAudioEl();
        if (!audio) {
            return;
        }
        if (self.isPlaying) {
            audio.pause();
            self.isPlaying = false;
        } else {
            audio.play();
            self.isPlaying = true;
        }
    };

    self.onTimeUpdate = () => {
        const audio = getAudioEl();
        if (!audio) {
            return;
        }
        self.currentTimeSec = Math.floor(audio.currentTime || 0);
        $scope.$evalAsync();
    };

    self.onLoadedMetadata = () => {
        const audio = getAudioEl();
        if (!audio) {
            return;
        }
        self.durationSec = Math.floor(audio.duration || 0);
        $scope.$evalAsync();
    };

    self.onEnded = () => {
        self.isPlaying = false;
        self.currentTimeSec = 0;
        $scope.$evalAsync();
    };

    self.onSeek = () => {
        const audio = getAudioEl();
        if (!audio || !audio.duration) {
            return;
        }
        audio.currentTime = Math.max(0, Math.min(self.currentTimeSec, audio.duration));
    };

    self.toggleSpeed = () => {
        self.playbackRate = self.playbackRate === 2 ? 1 : 2;
        const audio = getAudioEl();
        if (audio) {
            audio.playbackRate = getSafePlaybackRate(self.playbackRate);
        }
    };

    self.download = async () => {
        const src = self.src;
        const fallbackOpen = () => {
            try {
                const anchor = document.createElement('a');
                anchor.href = src;
                anchor.download = '';
                anchor.target = '_blank';
                anchor.rel = 'noopener noreferrer';
                anchor.click();
            } catch (_) {}
        };

        try {
            // Direct fetch (requires CORS); fall back if blocked
            const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
            if (!response.ok) {
                fallbackOpen();
                return;
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const fileName = (src.split('/').pop() || 'audio.mp3').split('?')[0] || 'audio.mp3';
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (_) {
            fallbackOpen();
        }
    };

    self.formatTime = (totalSeconds) => {
        if (!isFinite(totalSeconds)) {
            return '0:00';
        }
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return minutes + ':' + String(seconds).padStart(2, '0');
    };

    self.$onDestroy = () => {
        const audio = getAudioEl();
        if (audio) {
            // Detach DOM event listeners
            if (self._timeUpdateHandler) {
                audio.removeEventListener('timeupdate', self._timeUpdateHandler);
            }
            if (self._loadedMetadataHandler) {
                audio.removeEventListener('loadedmetadata', self._loadedMetadataHandler);
            }
            if (self._endedHandler) {
                audio.removeEventListener('ended', self._endedHandler);
            }
            audio.pause();
            audio.src = '';
            audio.load();
        }
    };
}

export function HuiAudioPlayer() {
    return {
        restrict: 'E',
        replace: true,
        scope: true,
        bindToController: {
            src: '@',
            color: '@',
            sizing: '@',
            playbackRate: '@',
            showSpeed: '<',
            showDownload: '<'
        },
        controller: Controller,
        controllerAs: '$huiAudioCtrl',
        transclude: false,
        template: ($element, $attrs) => {
            return `
            <div class="hui hui-audio-player d-flex align-items-center gap-2">
                <hui-button variant="solid" 
                            sizing="{{($huiAudioCtrl.sizing || 'md')}}"
                            color="{{($huiAudioCtrl.color || 'accent')}}"
                            ng-click="$huiAudioCtrl.togglePlay()">
                           <i class="fa" ng-class="$huiAudioCtrl.isPlaying ? 'fa-pause' : 'fa-play'"></i>
                </hui-button>
                <div class="d-flex align-items-center gap-1 flex-grow-1">
                    <span class="text-muted small time-label">{{$huiAudioCtrl.formatTime($huiAudioCtrl.currentTimeSec)}}</span>
                
                    <hui-input-slider min="0" max="$huiAudioCtrl.durationSec || 0" step="1"
                                      sizing="{{$huiAudioCtrl.sizing || 'md'}}"
                                      color="{{$huiAudioCtrl.color || 'accent'}}"
                                      ng-model="$huiAudioCtrl.currentTimeSec"
                                      on-change="$huiAudioCtrl.onSeek()"></hui-input-slider>
                    <span class="text-muted small duration-label">{{$huiAudioCtrl.formatTime($huiAudioCtrl.durationSec)}}</span>
                </div>
                <div class="d-flex align-items-center gap-2 ml-auto">
                    <span ng-if="$huiAudioCtrl.showSpeed !== false"
                          class="text-primary semi-bold speed-toggle"
                          ng-click="$huiAudioCtrl.toggleSpeed()">
                          x{{$huiAudioCtrl.playbackRate || 1}}</span>
                    <hui-button ng-if="$huiAudioCtrl.showDownload !== false" variant="outline" color="primary"
                                aria-label="Download" ng-click="$huiAudioCtrl.download()">
                                <i class="fa-solid fa-download"></i>
                    </hui-button>
                </div>
                <audio ng-src="{{$huiAudioCtrl.src}}"></audio>
            </div>
             `;
        }
    };
}